import React from 'react';
import {projectsApi} from '@/api/projects';
import type {CatalogElementHit, CatalogPackageNode} from '@/types/api';

const DEBOUNCE_MS = 250;

export function useProjectCatalog(opts: {
	projectId?: number;
	diagramId?: number;
	enabled?: boolean;
}) {
	const {projectId, diagramId, enabled = true} = opts;
	const [query, setQuery] = React.useState('');
	const [packageId, setPackageId] = React.useState<string | null>(null);
	const [items, setItems] = React.useState<CatalogElementHit[]>([]);
	const [packages, setPackages] = React.useState<CatalogPackageNode[]>([]);
	const [loading, setLoading] = React.useState(false);

	React.useEffect(() => {
		if (!enabled || !projectId) return;
		let cancelled = false;
		projectsApi.catalogPackages(projectId).then(res => {
			if (cancelled || res.error || !res.data) return;
			setPackages(res.data);
		});
		return () => {
			cancelled = true;
		};
	}, [enabled, projectId]);

	React.useEffect(() => {
		if (!enabled || !projectId) return;
		const q = query.trim();
		if (!q && !packageId) {
			setItems([]);
			setLoading(false);
			return;
		}
		setLoading(true);
		const timer = setTimeout(() => {
			projectsApi
				.searchCatalog(projectId, {
					q: q || undefined,
					packageId: packageId ?? undefined,
					diagramId,
					limit: 50,
				})
				.then(res => {
					setLoading(false);
					if (res.error || !res.data) {
						setItems([]);
						return;
					}
					setItems(res.data.items ?? []);
				});
		}, DEBOUNCE_MS);
		return () => {
			clearTimeout(timer);
		};
	}, [enabled, projectId, diagramId, query, packageId]);

	return {query, setQuery, packageId, setPackageId, items, packages, loading};
}
